import React, { useState } from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    eventType: '',
    eventDate: null,
  });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEventTypeChange = value => {
    setFormData(prev => ({ ...prev, eventType: value }));
  };

  const formatDateSafe = date => {
    if (!date) return null;
    const d = Array.isArray(date) ? date[0] : date;
    if (d instanceof Date && !isNaN(d)) return format(d, 'PPP');
    const parsed = new Date(d);
    return isNaN(parsed) ? null : format(parsed, 'PPP');
  };

  const formattedEventDate = formatDateSafe(formData.eventDate);

  const handleSubmit = e => {
    e.preventDefault();
    setSubmitStatus(null);

    if (!formData.eventType || !formattedEventDate) {
      setSubmitStatus('invalid');
      return;
    }

    const inquiry = [
      'Hi Frames by Gaurav, I would like to check photography availability.',
      `Name: ${formData.name}`,
      `Phone: ${formData.phone}`,
      `City: ${formData.city}`,
      `Event: ${formData.eventType}`,
      `Event date: ${formattedEventDate}`,
    ].join('\n');
    const whatsappUrl = `https://wa.me/917398413465?text=${encodeURIComponent(inquiry)}`;
    const whatsappWindow = window.open(whatsappUrl, '_blank');

    if (!whatsappWindow) {
      setSubmitStatus('blocked');
      return;
    }

    whatsappWindow.opener = null;
    setSubmitStatus('success');
  };

  return (
    <>
      <SEO
        title="Contact a Photographer in Lucknow | Frames by Gaurav"
        description="Check availability for wedding or event photography in Lucknow. Call or message Frames by Gaurav to discuss your date."
        keywords="contact photographer lucknow, book wedding photographer, photography studio contact, frames by gaurav contact, photographer near me lucknow"
        canonicalUrl="/contact"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact - Frames by Gaurav',
          description: 'Get in touch with us for your photography needs.',
          url: 'https://www.framesbygaurav.com/contact',
        }}
      />
      <Layout>
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-bold text-center mb-6 tracking-tight">
              Get In Touch
            </h1>
            <p className="text-center text-gray-600 text-lg mb-16 max-w-2xl mx-auto">
              Ready to start your photography journey with us? Fill out the form
              below and we&apos;ll get back to you shortly
            </p>

            {/* Contact Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold uppercase tracking-wider mb-2 text-black"
                >
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 focus:border-black focus:outline-none transition-colors"
                  placeholder="Your full name"
                />
              </div>

              {/* City & Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="city"
                    className="block text-sm font-semibold uppercase tracking-wider mb-2 text-black"
                  >
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 focus:border-black focus:outline-none transition-colors"
                    placeholder="Your city"
                  />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-semibold uppercase tracking-wider mb-2 text-black"
                  >
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 focus:border-black focus:outline-none transition-colors"
                    placeholder="+91 7398413465"
                  />
                </div>
              </div>

              {/* Event Type & Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="eventType"
                    className="block text-sm font-semibold uppercase tracking-wider mb-2 text-black"
                  >
                    Event Type *
                  </label>
                  <Select
                    id="eventType"
                    value={formData.eventType}
                    onValueChange={handleEventTypeChange}
                    required
                  >
                    <SelectTrigger className="w-full h-12 border-2 border-gray-300 focus:border-black">
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wedding">Wedding</SelectItem>
                      <SelectItem value="engagement">Engagement</SelectItem>
                      <SelectItem value="corporate">Corporate Event</SelectItem>
                      <SelectItem value="birthday">Birthday Party</SelectItem>
                      <SelectItem value="fashion">Fashion Shoot</SelectItem>
                      <SelectItem value="commercial">
                        Commercial Shoot
                      </SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label
                    htmlFor="eventDate"
                    className="block text-sm font-semibold uppercase tracking-wider mb-2 text-black"
                  >
                    Event Date *
                  </label>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="eventDate"
                        variant="outline"
                        className="w-full h-12 justify-start text-left font-normal border-2 border-gray-300 hover:border-black focus:border-black focus:outline-none transition-colors"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                        <span className="truncate">
                          {formattedEventDate || (
                            <span className="text-gray-500">Pick a date</span>
                          )}
                        </span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-4" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.eventDate}
                        onSelect={day => {
                          setFormData(prev => ({ ...prev, eventDate: day }));
                          setTimeout(() => setCalendarOpen(false), 100);
                        }}
                        initialFocus
                        disabled={date =>
                          date < new Date(new Date().setHours(0, 0, 0, 0))
                        }
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-center pt-4">
                <button
                  type="submit"
                  className="bg-black text-white px-12 py-4 text-sm tracking-widest uppercase hover:bg-gray-900 transition-colors inline-flex items-center zoom-hover"
                >
                  Check availability on WhatsApp
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="text-center p-4 bg-gray-100 text-black">
                  Your inquiry is ready in WhatsApp. Send the message there to
                  finish your request.
                </div>
              )}
              {submitStatus === 'invalid' && (
                <div
                  role="alert"
                  className="text-center p-4 bg-red-50 text-red-800"
                >
                  Select an event type and date to continue.
                </div>
              )}
              {submitStatus === 'blocked' && (
                <div
                  role="alert"
                  className="text-center p-4 bg-red-50 text-red-800"
                >
                  Your browser blocked the WhatsApp window. Allow pop-ups and
                  submit again.
                </div>
              )}
            </form>
          </div>
        </section>

        {/* Contact Info Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-3 text-black">
                  Call Us
                </h3>
                <a
                  href="tel:+917398413465"
                  className="text-lg text-gray-700 hover:text-black transition-colors"
                >
                  +91 73984 13465
                </a>
              </div>
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-3 text-black">
                  Email Us
                </h3>
                <a
                  href="mailto:info@framesbygaurav.com"
                  className="text-lg text-gray-700 hover:text-black transition-colors"
                >
                  info@framesbygaurav.com
                </a>
              </div>
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-3 text-black">
                  WhatsApp
                </h3>
                <a
                  href="https://wa.me/917398413465"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg text-gray-700 hover:text-black transition-colors"
                >
                  Message Us
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Address and Map Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-12 tracking-tight">
              Visit Our Studio
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Address */}
              <div className="flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-6 tracking-tight">
                  Address
                </h3>
                <div className="space-y-4 text-gray-700">
                  <p className="text-lg leading-relaxed">
                    <strong>Frames by Gaurav</strong>
                    <br />
                    Shop No. 4, Sujanpura Crossing,
                    <br />
                    Alambagh,
                    <br />
                    Lucknow, UP-226005,
                    <br />
                    India
                  </p>
                  <p className="text-lg">
                    <strong>Phone:</strong>{' '}
                    <a
                      href="tel:+917398413465"
                      className="hover:text-black transition-colors"
                    >
                      +91 73984 13465
                    </a>
                  </p>
                  <p className="text-lg">
                    <strong>Email:</strong>{' '}
                    <a
                      href="mailto:info@framesbygaurav.com"
                      className="hover:text-black transition-colors"
                    >
                      info@framesbygaurav.com
                    </a>
                  </p>
                  <a
                    href="https://maps.google.com/?q=Shop+No.+4+Sujanpura+Crossing+Alambagh+Lucknow"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-black text-white px-8 py-3 text-sm tracking-widest uppercase hover:bg-gray-900 transition-colors mt-4"
                  >
                    Get Directions
                  </a>
                </div>
              </div>

              {/* Google Map */}
              <div className="w-full h-[400px] border-2 border-gray-200">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3559.481847287866!2d80.88949031504823!3d26.83951678315066!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399bfd991f32b0bb%3A0x93ccba8909978be7!2sAlambagh%2C%20Lucknow%2C%20Uttar%20Pradesh%20226005!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Frames by Gaurav Location"
                ></iframe>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default Contact;
